import { get, patch, post, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

export type ComparisonStatus = "queued" | "processing" | "ready" | "failed";
export type FindingRisk = "high" | "medium" | "low" | "none";
export type FindingClassification = "match" | "changed" | "missing" | "added";
export type FindingStatus = "open" | "accepted" | "dismissed";

export interface FindingSuggestion {
  kind: "edit" | "insert" | "delete";
  paragraphIndex: number;
  oldText?: string;
  newText?: string;
  comment: string;
}

export interface ComparisonFinding {
  id: string;
  order: number;
  standardRef: string | null;
  receivedRef: string | null;
  receivedParagraphStart: number | null;
  classification: FindingClassification;
  direction: "stricter" | "looser" | "neutral" | null;
  topic: string | null;
  risk: FindingRisk;
  favours: string | null;
  summary: string;
  explanation: string | null;
  standardQuote: string | null;
  receivedQuote: string | null;
  suggestion: FindingSuggestion | null;
  confidence: "high" | "medium" | "low" | null;
  status: FindingStatus;
  dismissReason: string | null;
}

export interface ComparisonCounts {
  high: number;
  medium: number;
  low: number;
  matches: number;
}

export interface ComparisonListItem {
  id: string;
  status: ComparisonStatus;
  standardName: string;
  standardVersion: number | null;
  receivedFileName: string;
  projectId: string;
  projectName: string | null;
  /** "compare" = the person's private "My comparisons" project */
  projectKind: string | null;
  standardAgreementId: string;
  ourRole: string | null;
  createdAt: string;
  createdBy: string | null;
  counts: ComparisonCounts | null;
}

export interface Comparison extends ComparisonListItem {
  receivedFileId: string;
  progress: { stage: "preparing" | "aligning" | "analysing" | "summarising"; done: number; total: number } | null;
  summary: {
    overall: "close" | "moderate" | "far";
    headline: string;
    topDeviations: string[];
    nextSteps: string[];
    counts: ComparisonCounts;
    incomplete?: number;
  } | null;
  error: string | null;
  focus: string | null;
  language: string;
  canEdit: boolean;
  redlineAvailable: boolean;
  findings: ComparisonFinding[];
}

export const startComparison = (input: {
  standardAgreementId: string;
  file?: File;
  receivedFileId?: string;
  projectId?: string;
  focus?: string;
  ourRole?: string;
  language?: string;
  force?: boolean;
}): Promise<ApiResponse<Comparison>> => {
  const form = new FormData();
  form.append("standardAgreementId", input.standardAgreementId);
  if (input.file) form.append("file", input.file);
  if (input.receivedFileId) form.append("receivedFileId", input.receivedFileId);
  if (input.projectId) form.append("projectId", input.projectId);
  if (input.focus?.trim()) form.append("focus", input.focus.trim());
  if (input.ourRole) form.append("ourRole", input.ourRole);
  if (input.language) form.append("language", input.language);
  if (input.force) form.append("force", "true");
  return post<Comparison>(`/comparisons`, form, null);
};

export const getComparison = (id: string): Promise<ApiResponse<Comparison>> =>
  get(`/comparisons/${id}`, null);

export const listComparisons = (filter: { projectId?: string; standardAgreementId?: string } = {}): Promise<
  ApiResponse<ComparisonListItem[]>
> => {
  const params = new URLSearchParams();
  if (filter.projectId) params.set("projectId", filter.projectId);
  if (filter.standardAgreementId) params.set("standardAgreementId", filter.standardAgreementId);
  const qs = params.toString();
  return get(`/comparisons${qs ? `?${qs}` : ""}`, null);
};

export const updateFinding = (
  comparisonId: string,
  findingId: string,
  body: { status: FindingStatus; dismissReason?: string; newText?: string },
): Promise<ApiResponse<Comparison>> => patch(`/comparisons/${comparisonId}/findings/${findingId}`, body, null);

export const acceptAllFindings = (
  comparisonId: string,
  risks: FindingRisk[] = ["high", "medium"],
): Promise<ApiResponse<Comparison>> => post(`/comparisons/${comparisonId}/findings/accept-all`, { risks }, null);

export const exportComparison = (
  comparisonId: string,
  format: "redline" | "clean" | "report",
): Promise<ApiResponse<{ downloadUrl: string; fileName: string; applied?: number; skipped?: number }>> =>
  post(`/comparisons/${comparisonId}/export`, { format }, null);

export const deleteComparison = (id: string): Promise<ApiResponse<void>> => remove(`/comparisons/${id}`, null);

/** The caller's chat about a comparison (created the first time) */
export const openComparisonChat = (id: string): Promise<ApiResponse<{ chatId: string; created: boolean }>> =>
  post(`/comparisons/${id}/chat`, {}, null);
